import {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
    formatHour,
    getRateMonthsHours,
    HOUR_PERIOD,
    MONTH_PERIOD,
    rectToCells,
} from 'services/rates/ratesCalendarUtils'
import {toast} from 'sonner'
import {useCreateRatesMutation, useEditRateMutation, useCreateRateMutation} from 'store/Store'
import {getDocumentId, mod} from 'utils'
import useFormRateLogic from './useFormRateLogic'
import {useNavigate} from 'react-router-dom'
import {Grid} from '@mui/material'
import FormField from 'components/Control/FormField'
import {CONSTANT} from 'constants/enum/nameRate'
import {ELECTRICITY} from 'constants/enum/meterType'
const useRatesCalendar = ({rates, methods, fields}) => {
    const navigate = useNavigate()

    const [selectedCells, setSelectedCells] = useState(new Set())
    const [highlightedLockedCells, setHighlightedLockedCells] = useState(new Set())
    const [hoveredLockedCells, setHoveredLockedCells] = useState(new Set())
    const [lockedRate, setLockedRate] = useState(null)
    const {fields: controlledFields} = useFormRateLogic({
        fields: fields || {},
        methods,
    })

    const {t, i18n} = useTranslation()
    const [editRate, {isLoading: isUpdatingStatus}] = useEditRateMutation()
    const [createRates, {isLoading: isCreateRatesLoading}] = useCreateRatesMutation()
    const [createRate, {isLoading: isCreateRateLoading}] = useCreateRateMutation()
    const {handleSubmit, watch} = methods
    const {category: categoryValue, name: nameValue, typeDay} = watch()
    const {lockedCellSet, lockedCellMap, rateCellSetMap} = useMemo(() => {
        if (!rates?.data)
            return {
                lockedCellSet: new Set(),
                lockedCellMap: new Map(),
                rateCellSetMap: new Map(),
            }
        const selections = new Set()
        const cellMap = new Map()
        const rateCells = new Map()
        rates.data.forEach((rate = {}) => {
            if (
                rate.category !== 'electricity' ||
                rate.name === 'constant' ||
                rate.typeDay !== typeDay
            )
                return

            const {months, hours} = getRateMonthsHours(rate)
            months.forEach((monthValue) => {
                const monthIndex = mod(Number(monthValue) - 1, MONTH_PERIOD)
                hours.forEach((hourValue) => {
                    const hourIndex = mod(Number(hourValue), HOUR_PERIOD)
                    if (!Number.isNaN(monthIndex) && !Number.isNaN(hourIndex)) {
                        const cellKey = `${monthIndex}:${hourIndex}`
                        selections.add(cellKey)
                        if (!cellMap.has(cellKey)) {
                            cellMap.set(cellKey, rate)
                        }
                        const rateId = getDocumentId(rate)
                        if (rateId) {
                            if (!rateCells.has(rateId)) {
                                rateCells.set(rateId, new Set())
                            }
                            rateCells.get(rateId).add(cellKey)
                        }
                    }
                })
            })
        })
        return {lockedCellSet: selections, lockedCellMap: cellMap, rateCellSetMap: rateCells}
    }, [rates, typeDay])
    const lockedCells = lockedCellSet
    const lockedCellDetails = lockedCellMap
    const lockedRateCells = rateCellSetMap

    useEffect(() => {
        setSelectedCells((prev) => {
            if (!prev.size) return new Set()
            const next = new Set()
            prev.forEach((cell) => {
                if (!lockedCells.has(cell)) {
                    next.add(cell)
                }
            })
            return next
        })
    }, [lockedCells])

    useEffect(() => {
        setHighlightedLockedCells((prev) => {
            if (!prev.size) return prev
            const next = new Set()
            prev.forEach((cell) => {
                if (lockedCells.has(cell)) {
                    next.add(cell)
                }
            })
            return next.size === prev.size ? prev : next
        })
    }, [lockedCells])

    useEffect(() => {
        setHoveredLockedCells((prev) => {
            if (!prev.size) return prev
            const next = new Set()
            prev.forEach((cell) => {
                if (lockedCells.has(cell)) {
                    next.add(cell)
                }
            })
            return next.size === prev.size ? prev : next
        })
    }, [lockedCells])

    const handleClearSelection = useCallback(() => {
        setSelectedCells(new Set())
    }, [])

    const getRateCellSet = useCallback(
        (rate) => {
            if (!rate) return new Set()
            const rateId = getDocumentId(rate)
            if (rateId && lockedRateCells.has(rateId)) {
                return new Set(lockedRateCells.get(rateId))
            }
            const {months, hours} = getRateMonthsHours(rate)
            const cells = new Set()
            months.forEach((monthValue) => {
                const monthIndex = mod(Number(monthValue) - 1, MONTH_PERIOD)
                hours.forEach((hourValue) => {
                    const hourIndex = mod(Number(hourValue), HOUR_PERIOD)
                    if (!Number.isNaN(monthIndex) && !Number.isNaN(hourIndex)) {
                        cells.add(`${monthIndex}:${hourIndex}`)
                    }
                })
            })
            return cells
        },
        [lockedRateCells]
    )

    const handleRectAction = useCallback(
        (rect, action = 'add') => {
            if (!rect) return
            const cells = rectToCells(rect)
            if (!cells.length) return
            const uniqueCells = Array.from(new Set(cells))
            setHoveredLockedCells(new Set())
            setSelectedCells((prev) => {
                const next = new Set(prev)
                uniqueCells.forEach((key) => {
                    if (lockedCells.has(key)) return
                    if (action === 'remove') {
                        next.delete(key)
                    } else {
                        next.add(key)
                    }
                })
                return next
            })
        },
        [lockedCells]
    )

    const handleLockedCellClick = useCallback(
        (cellKey) => {
            const rate = lockedCellDetails.get(cellKey)
            if (rate) {
                setHighlightedLockedCells(getRateCellSet(rate))
                setLockedRate(rate)
            }
        },
        [getRateCellSet, lockedCellDetails]
    )

    const handleLockedCellHover = useCallback(
        (cellKey) => {
            const rate = lockedCellDetails.get(cellKey)
            if (!rate) {
                setHoveredLockedCells(new Set())
                return
            }
            setHoveredLockedCells(getRateCellSet(rate))
        },
        [getRateCellSet, lockedCellDetails]
    )

    const handleLockedCellLeave = useCallback(() => {
        setHoveredLockedCells(new Set())
    }, [])

    const closeLockedRateDialog = useCallback(() => {
        setLockedRate(null)
        setHighlightedLockedCells(new Set())
        setHoveredLockedCells(new Set())
    }, [])

    const disableLockedRate = useCallback(() => {
        if (!lockedRate) return
        const payload = {_id: lockedRate._id, status: false}
        toast.promise(
            editRate(payload)
                .unwrap()
                .then((result) => {
                    setLockedRate(null)
                    setHighlightedLockedCells(new Set())
                    setHoveredLockedCells(new Set())
                    return result
                }),
            {
                loading: t('rates.calendar.lockedRate.disabling'),
                success: () => t('rates.calendar.lockedRate.disableSuccess'),
                error: (err) => err?.data?.error || t('rates.calendar.lockedRate.disableError'),
            }
        )
    }, [editRate, lockedRate, t])

    const onSubmit = handleSubmit((formData) => {
        if (formData.name === CONSTANT) {
            const request = createRate(formData)
                .unwrap()
                .then(() => {
                    navigate('/rates')
                })
            toast.promise(request, {
                loading: t('rates.calendar.saving'),
                success: () => t('rates.submit.create.success'),
                error: (err) => err?.data?.error || t('rates.submit.create.error'),
            })
            return
        }
        if (!selectedCells.size) {
            toast.error(t('rates.calendar.selection.empty'))
            return
        }
        const monthToHours = new Map()
        selectedCells.forEach((key) => {
            const [monthStr, hourStr] = key.split(':')
            const month = Number(monthStr)
            const hour = Number(hourStr)
            if (!monthToHours.has(month)) {
                monthToHours.set(month, new Set())
            }
            monthToHours.get(month).add(hour)
        })

        const signatureMap = new Map()
        monthToHours.forEach((hourSet, month) => {
            const hours = Array.from(hourSet).sort((a, b) => a - b)
            if (!hours.length) return
            const signature = hours.join(',')
            if (!signatureMap.has(signature)) {
                signatureMap.set(signature, {hours, months: []})
            }
            signatureMap.get(signature).months.push(month + 1)
        })

        const payloads = Array.from(signatureMap.values()).map(
            ({hours, months: groupedMonths}) => ({
                ...formData,
                months: groupedMonths.sort((a, b) => a - b),
                hours,
            })
        )

        const request = createRates(payloads)
            .unwrap()
            .then((result) => {
                // reset({status: true})
                setSelectedCells(new Set())
                // navigate('/rates')
                return result
            })

        toast.promise(request, {
            loading: t('rates.calendar.saving'),
            success: () => t('rates.submit.create.success'),
            error: (err) => err?.data?.error || t('rates.submit.create.error'),
        })
    })

    const generalFieldConfigs = useMemo(() => {
        const items = []

        const accountSection = controlledFields?.account
        const settingsSection = controlledFields?.settingSection
        const rateItems = controlledFields?.rateSection?.items || {}
        const typeDayField = controlledFields?.dateAndTimeSection?.items?.typeDay
        if (rateItems.status) items.push({...rateItems.status})

        if (settingsSection && !settingsSection.hidden) {
            const isGlobalField = settingsSection.items?.isGlobal
            if (isGlobalField) {
                items.push({...isGlobalField})
            }
        }
        if (accountSection && !accountSection.hidden) {
            const accountField = accountSection.items?.account
            if (accountField) {
                items.push({...accountField, sm: 6})
            }
        }

        if (rateItems.category) items.push({...rateItems.category})
        if (rateItems.name) items.push({...rateItems.name})

        if (!controlledFields?.dateAndTimeSection?.hidden && typeDayField) {
            items.push({...typeDayField})
        }

        if (rateItems.rate) items.push({...rateItems.rate})
        return items
    }, [controlledFields])

    const shouldShowCalendar = useMemo(
        () => categoryValue === ELECTRICITY && nameValue && nameValue !== CONSTANT && typeDay,
        [categoryValue, nameValue, typeDay]
    )

    const monthLabels = useMemo(
        () =>
            Array.from({length: MONTH_PERIOD}, (_, index) =>
                new Date(2000, index, 1).toLocaleString(i18n.language, {month: 'long'})
            ),
        [i18n.language]
    )

    const lockedRateMonthsHours = useMemo(() => {
        if (!lockedRate) return {months: [], hours: []}
        return getRateMonthsHours(lockedRate)
    }, [lockedRate])

    const isSubmitting = useMemo(() => {
        return isCreateRatesLoading || isCreateRateLoading
    }, [isCreateRatesLoading, isCreateRateLoading])

    const renderFieldsGrid = useCallback(
        (fields, keyPrefix) => (
            <Grid container spacing={2} sx={{mt: 1}}>
                {fields.map((field) => (
                    <Grid
                        item
                        key={`${keyPrefix}_${field.name}`}
                        xs={field?.xs || 12}
                        sm={field?.sm || 12}>
                        <FormField field={field} i18n="rates" />
                    </Grid>
                ))}
            </Grid>
        ),
        []
    )

    const selectionSummary = useMemo(() => {
        const count = selectedCells.size
        if (!count) {
            return t('rates.calendar.form.emptySummary')
        }
        const example = Array.from(selectedCells)[0]
        const [month, hour] = example.split(':').map(Number)
        const monthLabel = monthLabels[month]
        const hourLabel = formatHour(hour)
        return t('rates.calendar.selection.count', {count, month: monthLabel, hour: hourLabel})
    }, [selectedCells, monthLabels, t])

    const displayedSelectedCells = useMemo(() => {
        const combined = new Set(selectedCells)
        highlightedLockedCells.forEach((cell) => combined.add(cell))
        hoveredLockedCells.forEach((cell) => combined.add(cell))
        return combined
    }, [selectedCells, highlightedLockedCells, hoveredLockedCells])

    return {
        selectedCells,
        setSelectedCells,
        highlightedLockedCells,
        setHighlightedLockedCells,
        hoveredLockedCells,
        setHoveredLockedCells,
        lockedRate,
        setLockedRate,
        handleClearSelection,
        getRateCellSet,
        handleRectAction,
        handleLockedCellHover,
        handleLockedCellLeave,
        handleLockedCellClick,
        closeLockedRateDialog,
        lockedCells,
        lockedCellDetails,
        isUpdatingStatus,
        disableLockedRate,
        onSubmit,
        isSubmitting,
        generalFieldConfigs,
        shouldShowCalendar,
        monthLabels,
        lockedRateMonthsHours,
        selectionSummary,
        displayedSelectedCells,
        renderFieldsGrid,
    }
}

export default useRatesCalendar
